/**
 * Selecting a section of a lane to edit. Pure functions over a Google Maps
 * path (`{lat, lng}[]`) — no React, no map instance — so the awkward cases
 * (a lane that re-enters the box, a box over a lane endpoint) are cheap to
 * test directly.
 */

/** Boundary-inclusive, so a vertex exactly on the drawn edge is selected. */
export const isInsideBox = (point, box) =>
  point.lat >= box.south &&
  point.lat <= box.north &&
  point.lng >= box.west &&
  point.lng <= box.east;

/**
 * Maximal stretches of CONSECUTIVE vertices inside the box.
 *
 * Consecutive is the whole point. A lane that loops, runs a parallel leg, or
 * doubles back can enter the box more than once, and treating "every vertex
 * inside the box" as one selection would splice across the gap and delete the
 * geometry in between. Each run is offered separately instead, and the
 * operator picks the one they meant.
 */
export const findRuns = (path, box) => {
  const runs = [];
  let startIndex = null;

  path.forEach((point, index) => {
    const inside = isInsideBox(point, box);

    if (inside && startIndex === null) {
      startIndex = index;
    }
    if (!inside && startIndex !== null) {
      runs.push({ startIndex, endIndex: index - 1 });
      startIndex = null;
    }
  });

  if (startIndex !== null) {
    runs.push({ startIndex, endIndex: path.length - 1 });
  }

  if (runs.length > 0) return runs;

  // Nothing had a vertex inside. That is not the same as "the lane misses the
  // box": stored lanes carry 16–22 vertices across hundreds of kilometres, so
  // a box drawn around one specific stretch of road routinely contains no
  // vertex at all — the nearest are tens of kilometres away on either side.
  // Vertex-only detection reported "no part of the lane is inside that box"
  // while the operator was looking at the line crossing it.
  //
  // Only as a FALLBACK, never alongside vertex runs: a box that does contain
  // vertices also tends to have segments clipping its corners, and offering
  // those as extra choices would put runs in front of the operator that they
  // never meant to select.
  return crossingRuns(path, box);
};

/**
 * Does any part of this segment lie inside the box?
 *
 * Liang–Barsky parametric clipping. Both endpoints are known to be outside
 * the box by the time this is called, so a simple endpoint test cannot answer
 * it — the segment may pass clean through, entering one edge and leaving
 * another.
 *
 * Treats lat/lng as a plane. Over the length of one lane segment the error is
 * far below the precision of a hand-drawn rectangle, and this only decides
 * whether a stretch is offered for selection — never where any geometry goes.
 */
const segmentCrossesBox = (a, b, box) => {
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;

  let enter = 0;
  let exit = 1;

  // For each boundary: `p` is the segment's movement against that edge's
  // inward normal, `q` how far inside the edge the segment starts. `p === 0`
  // means parallel to it — then the segment is either wholly inside that
  // boundary or wholly outside, decided by `q` alone.
  const clip = (p, q) => {
    if (p === 0) return q >= 0;

    const t = q / p;
    if (p < 0) {
      if (t > exit) return false;
      if (t > enter) enter = t;
    } else {
      if (t < enter) return false;
      if (t < exit) exit = t;
    }
    return true;
  };

  return (
    clip(-dx, a.lng - box.west) &&
    clip(dx, box.east - a.lng) &&
    clip(-dy, a.lat - box.south) &&
    clip(dy, box.north - a.lat)
  );
};

/**
 * One run per segment crossing the box, each containing NO vertices.
 *
 * Expressed as `{ startIndex: i + 1, endIndex: i }` — deliberately "empty",
 * with the end before the start. That is not a sentinel; it is what the rest
 * of the pipeline already means by a run with nothing in it. `widenRun`
 * widens it by one vertex at each end as usual and lands on exactly the
 * segment's own endpoints as anchors, and `spliceSection` then replaces the
 * segment and nothing else. No special cases needed downstream.
 *
 * Each crossing is offered separately, for the same reason multiple vertex
 * runs are: splicing across the gap between them would delete the geometry in
 * between.
 */
const crossingRuns = (path, box) => {
  const runs = [];

  for (let index = 0; index < path.length - 1; index += 1) {
    if (segmentCrossesBox(path[index], path[index + 1], box)) {
      runs.push({ startIndex: index + 1, endIndex: index });
    }
  }

  return runs;
};

/**
 * The stretch of lane to draw as "selected" for a run.
 *
 * An ordinary run is its own vertices. A run with none — a crossed segment —
 * would slice to an empty array and highlight nothing, leaving the operator
 * with no visible selection on a stretch that is about to be re-routed. Its
 * visual extent is the segment that brackets it.
 */
export const runDisplayPath = (path, run) =>
  run.endIndex < run.startIndex
    ? path.slice(run.startIndex - 1, run.startIndex + 1)
    : path.slice(run.startIndex, run.endIndex + 1);

/**
 * Widen a run by one vertex at each end, giving anchors that sit OUTSIDE the
 * selection. Those anchors are what the re-snap routes between, which is why
 * the spliced result reconnects to untouched geometry instead of leaving a
 * gap.
 *
 * Every run is selectable — this never returns null. What varies is whether
 * an anchor exists on a given side. A run touching index 0 has no vertex
 * before it (`anchorStartIndex: null`, `movesStart: true`): its new position
 * has to come from the operator rather than from existing geometry, because
 * there is no outside vertex to anchor to. A run reaching `pathLength - 1` is
 * the symmetric case at the other end. A run spanning the whole path sets
 * both — re-routing the entire lane is legitimate, it just means both ends
 * are free. An interior run gets real anchors on both sides and both flags
 * false, unchanged from before.
 */
export const widenRun = (run, pathLength) => {
  const movesStart = run.startIndex <= 0;
  const movesEnd = run.endIndex >= pathLength - 1;

  return {
    anchorStartIndex: movesStart ? null : run.startIndex - 1,
    anchorEndIndex: movesEnd ? null : run.endIndex + 1,
    movesStart,
    movesEnd,
  };
};

/**
 * Put waypoints into the order they occur ALONG the section, rather than the
 * order the operator happened to click them.
 *
 * The routing service visits waypoints in the order it is given and does not
 * reorder them to find a sensible path. Click order is not that order: the
 * natural way to use this tool is to drop a few points, look at the result,
 * then go back and refine a stretch already passed — and a refinement appends
 * to the END of the list. The route then runs forward to the later points,
 * back up the corridor to the new one, and forward again.
 *
 * That produced a real, and genuinely baffling, failure on the Gordo →
 * Tuscaloosa lane: seven waypoints wove up and down the corridor, the
 * candidate crossed back over the original line repeatedly, and the result
 * read as "nothing was replaced and the editor invented a loop". Nothing was
 * wrong with the splice — the section it was handed was the weave.
 *
 * Each waypoint is matched to its nearest point on `referencePath` — the
 * lane's own geometry for the stretch being edited — and sorted by how far
 * ALONG that path the match sits.
 *
 * Along the path, not along the straight line between its ends. An earlier
 * version projected onto that straight line, which holds only while the
 * corridor runs roughly straight. On a hairpin, or any stretch that doubles
 * back, two waypoints placed in genuine road order can project onto that axis
 * in REVERSE order — so the fix for a backtracking route would itself have
 * produced one. Following the lane's own shape has no such failure mode: the
 * geometry defines the order, and that geometry is what the operator is
 * correcting.
 *
 * Sorted by position ALONG, never by distance FROM: a waypoint's whole
 * purpose may be to pull the corridor well away from where it currently runs,
 * so how far off the line it sits must not affect where it comes in the
 * sequence. Distance is used only to decide WHICH part of the lane a waypoint
 * belongs to, never where it sorts.
 *
 * Longitude is scaled by cos(latitude) first. A degree of longitude is about
 * 0.84 of a degree of latitude at 33°N, and without the correction both the
 * matching and the along-path measure are skewed for a diagonal corridor —
 * which is the only kind a lane edit ever has.
 *
 * The sort is STABLE, and two waypoints abreast of each other project to the
 * same distance: their relative order then stays the operator's own rather
 * than the comparison's arbitrary outcome.
 *
 * Both degenerate cases return the list untouched rather than inventing an
 * order: fewer than two waypoints, and coincident anchors (no axis to project
 * onto, which would divide by zero and sort on NaN).
 */
export const orderWaypointsAlong = (referencePath = [], waypoints = []) => {
  if (referencePath.length < 2 || waypoints.length < 2) return waypoints;

  const latScale = Math.cos(
    (((referencePath[0].lat + referencePath[referencePath.length - 1].lat) / 2) *
      Math.PI) /
      180,
  );
  const toXY = (point) => ({ x: point.lng * latScale, y: point.lat });

  /**
   * How far along `referencePath` the nearest point to `waypoint` sits,
   * measured in cumulative planar length.
   *
   * Walks every segment, projects onto each, and keeps the closest. The
   * projection is clamped to the segment so a waypoint beyond one end is
   * attributed to that end rather than to an imaginary extension of it.
   */
  const distanceAlong = (waypoint) => {
    const point = toXY(waypoint);

    let travelled = 0;
    let best = { gap: Infinity, at: 0 };

    for (let i = 0; i < referencePath.length - 1; i += 1) {
      const from = toXY(referencePath[i]);
      const to = toXY(referencePath[i + 1]);
      const segX = to.x - from.x;
      const segY = to.y - from.y;
      const segLengthSq = segX * segX + segY * segY;
      const segLength = Math.sqrt(segLengthSq);

      const t =
        segLengthSq === 0
          ? 0
          : Math.max(
              0,
              Math.min(
                1,
                ((point.x - from.x) * segX + (point.y - from.y) * segY) /
                  segLengthSq,
              ),
            );

      const dx = point.x - (from.x + t * segX);
      const dy = point.y - (from.y + t * segY);
      const gap = dx * dx + dy * dy;

      if (gap < best.gap) {
        best = { gap, at: travelled + t * segLength };
      }

      travelled += segLength;
    }

    return best.at;
  };

  return waypoints
    .map((point, index) => ({ point, index, along: distanceAlong(point) }))
    .sort((left, right) =>
      left.along === right.along
        ? left.index - right.index
        : left.along - right.along,
    )
    .map((entry) => entry.point);
};

/**
 * Replace everything between the two anchors (inclusive) with the snapped
 * section.
 *
 * `section` already begins at the start anchor and ends at the end anchor —
 * that is what was routed — so both anchor vertices are consumed by the
 * replacement rather than duplicated.
 *
 * Either anchor may be `null` — `widenRun` returns that for a run touching
 * the lane's start or end, where there is no outside vertex to anchor to. A
 * null start anchor means no prefix: the section itself supplies the new
 * start, so nothing from `path` precedes it. A null end anchor is the
 * symmetric case: no suffix. (Naively slicing on `null`/`NaN` does not do
 * this — `path.slice(0, null)` happens to work, but `path.slice(null + 1)` is
 * `path.slice(NaN)`, which returns the WHOLE array — so both null cases are
 * handled explicitly rather than relying on that coercion.)
 *
 * THIS DOES NOT PRESERVE THE LANE'S OWN ENDPOINTS, and the caller must not
 * assume it does. Even with a real anchor at index 0 (any run starting at
 * index 1), that anchor is consumed here, so the lane's first vertex is
 * REPLACED by the snapped section's own endpoint, which the routing service
 * may have moved to the nearest road. Endpoint preservation is the caller's
 * job: see `pinEndpoints`.
 */
export const spliceSection = (path, anchorStartIndex, anchorEndIndex, section) => [
  ...(anchorStartIndex === null ? [] : path.slice(0, anchorStartIndex)),
  ...section,
  ...(anchorEndIndex === null ? [] : path.slice(anchorEndIndex + 1)),
];

/**
 * Force a spliced path's first and last coordinates back to the original
 * path's, so the lane's true endpoints survive an edit that reached them —
 * UNLESS that end was ACTUALLY DRAGGED (`startDragged`/`endDragged`), in
 * which case pinning it would silently discard the operator's drag of the
 * terminus handle. The failure would look like "the handle does nothing", so
 * a dragged end is left alone; every other end gets pinned.
 *
 * `startDragged`/`endDragged` is deliberately NOT the same signal as
 * `widenRun`'s `movesStart`/`movesEnd`. `movesStart`/`movesEnd` says the
 * *selection* reaches that terminus — which is what `allowStartMove`/
 * `allowEndMove` on the commit is about: permission to move it. Whether the
 * terminus actually moved is a separate fact, decided by whether the
 * operator dragged the handle at all. Conflating the two would mean an
 * operator who selects a section reaching the start, without ever touching
 * the handle, gets the start silently shifted to wherever the routing
 * service happened to snap it — which contradicts "if the operator never
 * drags it, the terminus does not move".
 *
 * The backend enforces the staying-put invariant with a 1 m `ST_StartPoint` /
 * `ST_EndPoint` guard, so there is no corruption to fix — but a road-snapped
 * endpoint can easily drift further than 1 m, and the rejection then surfaces
 * to the operator as a misleading "changed since you loaded it". Pinning the
 * ends here makes the invariant hold STRUCTURALLY rather than by trusting the
 * routing service to hand back a coordinate it was never asked to preserve.
 */
export const pinEndpoints = (
  path,
  originalPath,
  { startDragged = false, endDragged = false } = {},
) => {
  if (path.length === 0 || originalPath.length === 0) return path;

  const pinned = [...path];
  if (!startDragged) pinned[0] = originalPath[0];
  if (!endDragged) pinned[pinned.length - 1] = originalPath[originalPath.length - 1];
  return pinned;
};

/**
 * Google Maps path -> GeoJSON LineString text.
 *
 * Note the transpose: Maps uses `{lat, lng}`, GeoJSON positions are
 * `[lng, lat]`. Getting this backwards produces a geometry that parses
 * cleanly and lands somewhere off the coast of Africa.
 */
export const pathToGeoJson = (path) =>
  JSON.stringify({
    type: 'LineString',
    coordinates: path.map(({ lat, lng }) => [lng, lat]),
  });
